export const PRODUCTION_PORTAL_ORIGIN = 'https://app.maap.care';
export const FAMILY_LOGIN_URL = 'https://www.activities.maap.care/family/login';

export const ACTIVITY_APP_ORIGINS = [
  'https://www.activities.maap.care',
  'https://activities.maap.care',
];

export const MEAL_APP_ORIGINS = [
  'https://www.menu.maap.care',
  'https://menu.maap.care',
];

export const PORTAL_ORIGINS = [
  'https://app.maap.care',
  'https://www.app.maap.care',
];

export function detectHostFamily(hostname = typeof window !== 'undefined' ? window.location.hostname : '') {
  const host = String(hostname || '').toLowerCase();
  if (host === 'localhost' || host === '127.0.0.1') return 'local';
  if (host === 'maap.care' || host.endsWith('.maap.care')) return 'maap';
  if (host === 'happyday.care' || host.endsWith('.happyday.care')) return 'happyday';
  return 'other';
}

export function shouldUsePortalAuth(loc = typeof window !== 'undefined' ? window.location : null) {
  if (!loc) return false;
  if (new URLSearchParams(loc.search).get('local') === '1') return false;
  return detectHostFamily(loc.hostname) === 'maap';
}

export function getPortalLoginUrl({ next } = {}) {
  const url = new URL('/', PRODUCTION_PORTAL_ORIGIN);
  if (next) url.searchParams.set('next', next);
  return url.toString();
}

export function getStaffLogoutUrl() {
  if (typeof window !== 'undefined' && shouldUsePortalAuth()) {
    const url = new URL('/', PRODUCTION_PORTAL_ORIGIN);
    url.searchParams.set('signed_out', '1');
    return url.toString();
  }
  return '/login';
}

export function redirectToStaffLogin({ next } = {}) {
  if (typeof window === 'undefined') return;
  if (shouldUsePortalAuth()) {
    const fallback = `${window.location.origin}${window.location.pathname}${window.location.search}`;
    window.location.replace(getPortalLoginUrl({ next: next || fallback }));
    return;
  }
  window.location.replace('/login');
}

export function redirectToFamilyLogin() {
  if (typeof window === 'undefined') return;
  window.location.replace(FAMILY_LOGIN_URL);
}

export function sanitizeAppPath(raw, fallback = '/') {
  const value = String(raw || '').trim() || fallback;
  if (!value.startsWith('/') || value.startsWith('//')) return fallback;
  if (value.includes('://')) return fallback;
  if (value.length > 1024) return fallback;
  return value;
}

function isLocalHost(hostname) {
  return hostname === 'localhost' || hostname === '127.0.0.1';
}

export function parseAllowlistedNext(raw) {
  const value = String(raw || '').trim();
  if (!value) return null;
  try {
    const url = new URL(value);
    const protocolOk = url.protocol === 'https:' || (url.protocol === 'http:' && isLocalHost(url.hostname));
    if (!protocolOk) return null;
    const origin = url.origin;
    const nextPath = sanitizeAppPath(`${url.pathname}${url.search}`);
    if (ACTIVITY_APP_ORIGINS.includes(origin) || (isLocalHost(url.hostname) && url.port === '4028')) {
      return { targetApp: 'activity', nextPath, origin };
    }
    if (MEAL_APP_ORIGINS.includes(origin) || (isLocalHost(url.hostname) && url.port === '5173')) {
      return { targetApp: 'meal', nextPath, origin };
    }
    if (PORTAL_ORIGINS.includes(origin)) {
      return { targetApp: null, nextPath: '/', origin: 'portal' };
    }
  } catch {
    return null;
  }
  return null;
}

export function currentAppHref() {
  if (typeof window === 'undefined') return '';
  return `${window.location.origin}${window.location.pathname}${window.location.search}`;
}

export function followLogoutDestination(dest) {
  if (typeof window === 'undefined') return;
  const target = dest || getStaffLogoutUrl();
  if (/^https?:\/\//i.test(target)) {
    window.location.replace(target);
    return;
  }
  window.location.replace(target);
}

export async function invokeSsoHandoff(body, { accessToken } = {}) {
  const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
  const anonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;
  if (!supabaseUrl || !anonKey) {
    throw new Error('SSO is not configured');
  }
  const headers = {
    'Content-Type': 'application/json',
    apikey: anonKey,
    Authorization: `Bearer ${accessToken || anonKey}`,
  };
  const response = await fetch(`${String(supabaseUrl).replace(/\/$/, '')}/functions/v1/sso-handoff`, {
    method: 'POST',
    headers,
    body: JSON.stringify(body),
  });
  let payload = null;
  try {
    payload = await response.json();
  } catch {
    payload = null;
  }
  if (!response.ok) {
    const detail = payload?.reason ? ` (${payload.reason})` : '';
    const err = new Error(`${payload?.error || 'Handoff failed'}${detail}`);
    err.status = response.status;
    err.reason = payload?.reason;
    throw err;
  }
  return payload;
}

export async function createSsoHandoff(supabase, { targetApp, nextPath = '/' } = {}) {
  const { data, error } = await supabase.auth.getSession();
  // Only the access JWT is sent — Edge verifies it and mints the app session on consume.
  // Never send refresh_token (unbound / must not be stored server-side).
  if (error || !data?.session?.access_token) {
    throw new Error('Not signed in');
  }
  return invokeSsoHandoff({
    action: 'create',
    target_app: targetApp,
    next_path: sanitizeAppPath(nextPath),
  }, { accessToken: data.session.access_token });
}

export async function consumeSsoHandoff({ ticket, targetApp }) {
  return invokeSsoHandoff({
    action: 'consume',
    ticket,
    target_app: targetApp,
  });
}
